import React, { useState, useEffect } from 'react';
import { Bell, Plus, Edit2, Trash2, X, Eye, AlertTriangle } from 'lucide-react';
import { supabase } from '../supabase';

const getAdminEmail = () => {
  try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch(e) { return null; }
};

const TIER_OPTIONS = ['affiliate', 'recruiter', 'director'];

const emptyForm = {
  title: '',
  body: '',
  target_tiers: [],
  priority: 'normal',
  expires_at: '',
  active: true
};

export default function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const { data: rows, error } = await supabase
        .from('affiliate_announcements')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get read counts for all announcements
      const ids = (rows || []).map(r => r.id);
      let readCounts = {};
      if (ids.length > 0) {
        const { data: reads } = await supabase
          .from('affiliate_announcement_reads')
          .select('announcement_id')
          .in('announcement_id', ids);

        (reads || []).forEach(r => {
          readCounts[r.announcement_id] = (readCounts[r.announcement_id] || 0) + 1;
        });
      }

      setAnnouncements((rows || []).map(a => ({ ...a, read_count: readCounts[a.id] || 0 })));
    } catch (err) {
      console.error('Error loading announcements:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (ann) => {
    setEditingId(ann.id);
    setForm({
      title: ann.title || '',
      body: ann.body || '',
      target_tiers: ann.target_tiers || [],
      priority: ann.priority || 'normal',
      expires_at: ann.expires_at ? ann.expires_at.split('T')[0] : '',
      active: ann.active !== false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const toggleTier = (tier) => {
    setForm(prev => ({
      ...prev,
      target_tiers: prev.target_tiers.includes(tier)
        ? prev.target_tiers.filter(t => t !== tier)
        : [...prev.target_tiers, tier]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);

    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        target_tiers: form.target_tiers,
        priority: form.priority,
        expires_at: form.expires_at || null,
        active: form.active
      };

      if (editingId) {
        const { error } = await supabase
          .from('affiliate_announcements')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
      } else {
        payload.created_by = getAdminEmail();
        const { error } = await supabase
          .from('affiliate_announcements')
          .insert(payload);
        if (error) throw error;
      }

      closeModal();
      await loadAnnouncements();
    } catch (err) {
      console.error('Error saving announcement:', err);
      alert('Failed to save announcement: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      // Delete reads first (foreign key)
      await supabase
        .from('affiliate_announcement_reads')
        .delete()
        .eq('announcement_id', id);

      const { error } = await supabase
        .from('affiliate_announcements')
        .delete()
        .eq('id', id);
      if (error) throw error;

      setDeletingId(null);
      await loadAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const truncate = (str, len = 80) => {
    if (!str) return '';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  // Styles
  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    createBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '10px 18px',
      background: '#ff6b35',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600'
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: '0',
      background: '#1a1a1a',
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid #2a2a2a'
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      fontSize: '12px',
      fontWeight: '600',
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: '1px solid #2a2a2a',
      background: '#151515'
    },
    td: {
      padding: '14px 16px',
      fontSize: '14px',
      color: '#ccc',
      borderBottom: '1px solid #2a2a2a',
      verticalAlign: 'middle'
    },
    titleCell: {
      fontWeight: '600',
      color: '#fff'
    },
    badge: (type) => ({
      display: 'inline-block',
      padding: '3px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      ...(type === 'urgent'
        ? { background: 'rgba(239,68,68,0.15)', color: '#ef4444' }
        : { background: 'rgba(255,107,53,0.12)', color: '#ff6b35' })
    }),
    activeBadge: (active) => ({
      display: 'inline-block',
      padding: '3px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '600',
      ...(active
        ? { background: 'rgba(34,197,94,0.15)', color: '#22c55e' }
        : { background: 'rgba(150,150,150,0.15)', color: '#888' })
    }),
    tierTag: {
      display: 'inline-block',
      padding: '2px 6px',
      borderRadius: '3px',
      fontSize: '11px',
      fontWeight: '500',
      background: 'rgba(255,255,255,0.06)',
      color: '#aaa',
      marginRight: '4px'
    },
    readCount: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      color: '#888',
      fontSize: '13px'
    },
    iconBtn: (color = '#888') => ({
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '6px',
      borderRadius: '6px',
      color,
      display: 'inline-flex',
      alignItems: 'center'
    }),
    actions: {
      display: 'flex',
      gap: '4px',
      alignItems: 'center'
    },
    // Modal styles
    overlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modal: {
      background: '#1a1a1a',
      border: '1px solid #2a2a2a',
      borderRadius: '12px',
      padding: '24px',
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#fff'
    },
    label: {
      display: 'block',
      fontSize: '13px',
      fontWeight: '600',
      color: '#aaa',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      background: '#0a0a0a',
      border: '1px solid #2a2a2a',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      background: '#0a0a0a',
      border: '1px solid #2a2a2a',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '14px',
      outline: 'none',
      minHeight: '120px',
      resize: 'vertical',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      background: '#0a0a0a',
      border: '1px solid #2a2a2a',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box'
    },
    fieldGroup: {
      marginBottom: '16px'
    },
    checkboxRow: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px',
      color: '#ccc',
      cursor: 'pointer'
    },
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    toggle: (on) => ({
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      background: on ? '#ff6b35' : '#333',
      border: 'none',
      cursor: 'pointer',
      position: 'relative',
      transition: 'background 0.2s'
    }),
    toggleDot: (on) => ({
      position: 'absolute',
      top: '3px',
      left: on ? '23px' : '3px',
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      background: '#fff',
      transition: 'left 0.2s'
    }),
    submitBtn: {
      width: '100%',
      padding: '12px',
      background: '#ff6b35',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '8px'
    },
    // Delete confirmation
    deleteOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100
    },
    deleteModal: {
      background: '#1a1a1a',
      border: '1px solid #2a2a2a',
      borderRadius: '12px',
      padding: '24px',
      width: '400px',
      maxWidth: '95vw',
      textAlign: 'center'
    },
    deleteText: {
      color: '#ccc',
      fontSize: '14px',
      marginBottom: '20px',
      lineHeight: '1.5'
    },
    deleteBtns: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'center'
    },
    cancelBtn: {
      padding: '10px 20px',
      background: '#2a2a2a',
      color: '#ccc',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    },
    confirmDeleteBtn: {
      padding: '10px 20px',
      background: '#ef4444',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600'
    },
    empty: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#666'
    },
    emptyIcon: {
      marginBottom: '12px',
      color: '#444'
    },
    loadingText: {
      textAlign: 'center',
      padding: '60px',
      color: '#666',
      fontSize: '14px'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading announcements...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          <Bell size={22} color="#ff6b35" />
          Announcements
        </div>
        <button style={styles.createBtn} onClick={openCreate}>
          <Plus size={16} /> New Announcement
        </button>
      </div>

      {/* Table */}
      {announcements.length === 0 ? (
        <div style={{ ...styles.table, ...styles.empty }}>
          <div style={styles.emptyIcon}><Bell size={40} /></div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#888', marginBottom: '6px' }}>
            No announcements yet
          </div>
          <div style={{ fontSize: '13px', color: '#555' }}>
            Create one to notify affiliates.
          </div>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Body</th>
              <th style={styles.th}>Priority</th>
              <th style={styles.th}>Tiers</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Reads</th>
              <th style={styles.th}>Created</th>
              <th style={styles.th}>Expires</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {announcements.map((ann, idx) => (
              <tr key={ann.id} style={idx === announcements.length - 1 ? { borderBottom: 'none' } : {}}>
                <td style={{ ...styles.td, ...styles.titleCell, minWidth: '160px' }}>
                  {ann.title}
                </td>
                <td style={{ ...styles.td, maxWidth: '240px' }}>
                  {truncate(ann.body)}
                </td>
                <td style={styles.td}>
                  <span style={styles.badge(ann.priority)}>
                    {ann.priority === 'urgent' && <AlertTriangle size={11} style={{ marginRight: '3px', verticalAlign: 'middle' }} />}
                    {ann.priority || 'normal'}
                  </span>
                </td>
                <td style={styles.td}>
                  {(ann.target_tiers || []).length > 0
                    ? ann.target_tiers.map(t => (
                        <span key={t} style={styles.tierTag}>{t}</span>
                      ))
                    : <span style={{ color: '#555', fontSize: '12px' }}>all</span>
                  }
                </td>
                <td style={styles.td}>
                  <span style={styles.activeBadge(ann.active)}>
                    {ann.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={styles.readCount}>
                    <Eye size={14} />
                    {ann.read_count}
                  </div>
                </td>
                <td style={styles.td}>
                  {formatDate(ann.created_at)}
                </td>
                <td style={styles.td}>
                  {ann.expires_at ? formatDate(ann.expires_at) : <span style={{ color: '#555' }}>--</span>}
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <div style={{ ...styles.actions, justifyContent: 'flex-end' }}>
                    <button
                      style={styles.iconBtn('#ff6b35')}
                      title="Edit"
                      onClick={() => openEdit(ann)}
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      style={styles.iconBtn('#ef4444')}
                      title="Delete"
                      onClick={() => setDeletingId(ann.id)}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={styles.overlay} onClick={closeModal}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                {editingId ? 'Edit Announcement' : 'New Announcement'}
              </div>
              <button style={styles.iconBtn('#888')} onClick={closeModal}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Title *</label>
                <input
                  style={styles.input}
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Announcement title"
                  required
                />
              </div>

              {/* Body */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Body</label>
                <textarea
                  style={styles.textarea}
                  value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  placeholder="Announcement details..."
                />
              </div>

              {/* Target Tiers */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Target Tiers</label>
                <div style={styles.checkboxRow}>
                  {TIER_OPTIONS.map(tier => (
                    <label key={tier} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={form.target_tiers.includes(tier)}
                        onChange={() => toggleTier(tier)}
                        style={{ accentColor: '#ff6b35' }}
                      />
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                  Leave all unchecked to target everyone.
                </div>
              </div>

              {/* Priority + Expires row */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Priority</label>
                  <select
                    style={styles.select}
                    value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                  >
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Expires At</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={form.expires_at}
                    onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div style={{ ...styles.fieldGroup, ...styles.toggleRow }}>
                <label style={{ ...styles.label, marginBottom: 0 }}>Active</label>
                <button
                  type="button"
                  style={styles.toggle(form.active)}
                  onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                >
                  <div style={styles.toggleDot(form.active)} />
                </button>
              </div>

              {/* Submit */}
              <button
                type="submit"
                style={{
                  ...styles.submitBtn,
                  opacity: saving ? 0.6 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : editingId ? 'Update Announcement' : 'Create Announcement'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deletingId && (
        <div style={styles.deleteOverlay} onClick={() => setDeletingId(null)}>
          <div style={styles.deleteModal} onClick={e => e.stopPropagation()}>
            <AlertTriangle size={36} color="#ef4444" style={{ marginBottom: '12px' }} />
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff', marginBottom: '8px' }}>
              Delete Announcement?
            </div>
            <div style={styles.deleteText}>
              This will permanently delete this announcement and all associated read records. This cannot be undone.
            </div>
            <div style={styles.deleteBtns}>
              <button style={styles.cancelBtn} onClick={() => setDeletingId(null)}>
                Cancel
              </button>
              <button style={styles.confirmDeleteBtn} onClick={() => handleDelete(deletingId)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
